import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  onboardingService, OnbTemplateContent, emptyTemplateContent,
  OnbTemplateStage, OnbTemplateMeeting, OnbTemplateAgendaItem, OnbTemplateUser, OnbTemplatePortalPage,
  OnbTemplateResource, OnbTemplateFile, OnbTemplateNote, OnbTemplateSettings, OnbTemplateFolder,
} from '../services/onboardingService';
import { organizationService, OrgMember } from '../services/organizationService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/OnboardingTemplateBuilder.css';

const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);
const initials = (n?: string | null) => (n || 'U').trim().split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();

type TabKey = 'tasks' | 'meetings' | 'users' | 'portal' | 'files' | 'notes' | 'settings';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'tasks',    label: 'Tasks' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'users',    label: 'Users' },
  { key: 'portal',   label: 'Portal' },
  { key: 'files',    label: 'Files' },
  { key: 'notes',    label: 'Notes' },
  { key: 'settings', label: 'Settings' },
];

const OnboardingTemplateBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrg } = useAuth();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('Untitled Template');
  const [isPublic, setIsPublic] = useState(false);
  const [renamingTitle, setRenamingTitle] = useState(false);
  const [content, setContent] = useState<OnbTemplateContent>(emptyTemplateContent());
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [folders, setFolders] = useState<OnbTemplateFolder[]>([]);
  const [srcFolderId, setSrcFolderId] = useState<string | null>(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('tasks');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('saved');

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);

  // ── Load ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) { setNotFound(true); setLoading(false); return; }
      try {
        const t = await onboardingService.getProjectTemplate(id);
        if (cancelled) return;
        if (!t) { setNotFound(true); return; }
        setName(t.name);
        setIsPublic(t.isPublic);
        setSrcFolderId(t.folderId);
        setContent(t.content);
      } catch (err) {
        console.error('Failed to load template:', err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!currentOrg?.id) return;
    organizationService.getMembersWithProfiles(currentOrg.id).then(setMembers).catch(() => {});
    onboardingService.listTemplateFolders().then(setFolders).catch(() => {});
  }, [currentOrg?.id]);

  // ── Debounced autosave of content ──
  const markDirty = () => {
    dirtyRef.current = true;
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 800);
  };
  const flush = async () => {
    if (!id || !dirtyRef.current) return;
    dirtyRef.current = false;
    try {
      await onboardingService.saveTemplateContent(id, contentRef.current);
      setSaveState('saved');
    } catch (err) { console.error('Save failed:', err); setSaveState('idle'); }
  };
  // keep a ref of latest content so the debounced flush persists newest state
  const contentRef = useRef(content);
  useEffect(() => { contentRef.current = content; }, [content]);

  const update = (patch: Partial<OnbTemplateContent>) => {
    setContent(prev => { const next = { ...prev, ...patch }; contentRef.current = next; return next; });
    markDirty();
  };

  const saveName = async (v: string) => {
    const nm = v.trim() || 'Untitled Template';
    setName(nm); setRenamingTitle(false);
    if (id) { try { await onboardingService.updateProjectTemplate(id, { name: nm }); } catch (e) { console.error(e); } }
  };

  const togglePublic = async (v: boolean) => {
    setIsPublic(v);
    if (id) { try { await onboardingService.updateProjectTemplate(id, { isPublic: v }); } catch (e) { console.error(e); setIsPublic(!v); } }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const doDelete = async () => {
    if (!id) return;
    try { await onboardingService.deleteProjectTemplate(id); navigate('/onboarding/templates'); }
    catch (err: any) { alert(`Failed to delete: ${err?.message ?? err}`); }
  };
  // Duplicate into a chosen folder. `folder` is an existing folder id, null for
  // Uncategorized, or { newName } to create a folder first.
  const doDuplicate = async (target: { folderId: string | null } | { newFolder: string }) => {
    if (!id) return;
    // Snapshot the *current* template's content before touching anything, and
    // cancel/flush any pending autosave so it lands on THIS template — never on
    // the copy — so the original is preserved and both survive.
    const snapshot = contentRef.current;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    try {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        await onboardingService.saveTemplateContent(id, snapshot);
        setSaveState('saved');
      }
      let folderId: string | null;
      if ('newFolder' in target) {
        const f = await onboardingService.createTemplateFolder(target.newFolder);
        folderId = f.id;
      } else {
        folderId = target.folderId;
      }
      const copy = await onboardingService.createProjectTemplate({ name: `${name} (copy)`, folderId, isPublic: false });
      await onboardingService.saveTemplateContent(copy.id, snapshot);
      setDupOpen(false);
      navigate(`/onboarding/templates/${copy.id}/edit`);
    } catch (err: any) { alert(`Failed to duplicate: ${err?.message ?? err}`); }
  };

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  if (loading) {
    return <Layout><div className="tb-page"><div className="tb-loading">Loading template…</div></div></Layout>;
  }
  if (notFound) {
    return (
      <Layout>
        <div className="tb-page">
          <div className="tb-loading">
            Template not found. <button className="tb-link" onClick={() => navigate('/onboarding/templates')}>Back to templates</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="tb-page">
        {/* Header */}
        <div className="tb-header">
          <div className="tb-header-left">
            <button className="tb-back" onClick={() => { flush(); navigate('/onboarding/templates'); }} title="Back to templates">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            </button>
            <span className="tb-title-icon">
              {content.settings?.icon
                ? <img src={content.settings.icon} alt="" className="tb-title-icon-img" />
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 8h6M9 12h6M9 16h3" /></svg>}
            </span>
            {renamingTitle ? (
              <input
                className="tb-title-input" autoFocus defaultValue={name}
                onBlur={e => saveName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setRenamingTitle(false); }}
              />
            ) : (
              <button className="tb-title" onClick={() => setRenamingTitle(true)} title="Rename template">{name}</button>
            )}
          </div>
          <div className="tb-header-right">
            <span className={`tb-save-status ${saveState}`}>
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'All changes saved' : 'Unsaved'}
            </span>
            <button className="btn-primary-cta" onClick={() => alert('Creating a project from a template is coming soon.')}>Create Project from Template</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="tb-tabs" role="tablist">
          {TABS.map(t => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} className={`tb-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="tb-body">
          {tab === 'tasks'    && <TasksTab    content={content} update={update} />}
          {tab === 'meetings' && <MeetingsTab content={content} update={update} />}
          {tab === 'users'    && <UsersTab    content={content} update={update} members={members} />}
          {tab === 'portal'   && <PortalTab   content={content} update={update} />}
          {tab === 'files'    && <FilesTab    content={content} update={update} />}
          {tab === 'notes'    && <NotesTab    content={content} update={update} />}
          {tab === 'settings' && <SettingsTab content={content} update={update} members={members} name={name} isPublic={isPublic} onTogglePublic={togglePublic} onRename={saveName} onDuplicate={() => setDupOpen(true)} onDelete={() => setConfirmDelete(true)} />}
        </div>
      </div>

      {confirmDelete && (
        <div className="tb-modal-backdrop" onClick={() => setConfirmDelete(false)}>
          <div className="tb-confirm" onClick={e => e.stopPropagation()}>
            <div className="tb-confirm-ic"><TrashIc /></div>
            <h3 className="tb-confirm-title">Delete this template?</h3>
            <p className="tb-confirm-text"><strong>{name}</strong> and all of its content will be permanently removed. This can’t be undone.</p>
            <div className="tb-confirm-actions">
              <button className="tb-btn-ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="tb-btn-danger" onClick={doDelete}>Delete template</button>
            </div>
          </div>
        </div>
      )}

      {dupOpen && (
        <DuplicateModal
          name={name}
          folders={folders}
          defaultFolderId={srcFolderId}
          onClose={() => setDupOpen(false)}
          onDuplicate={doDuplicate}
        />
      )}
    </Layout>
  );
};

/* ── Duplicate-to-folder modal ── */
const DuplicateModal: React.FC<{
  name: string;
  folders: OnbTemplateFolder[];
  defaultFolderId: string | null;
  onClose: () => void;
  onDuplicate: (t: { folderId: string | null } | { newFolder: string }) => void;
}> = ({ name, folders, defaultFolderId, onClose, onDuplicate }) => {
  const UNCAT = '__uncat__';
  const NEW = '__new__';
  const [choice, setChoice] = useState<string>(defaultFolderId ?? UNCAT);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const go = () => {
    setBusy(true);
    if (choice === NEW) {
      const nm = newName.trim();
      if (!nm) { setBusy(false); return; }
      onDuplicate({ newFolder: nm });
    } else {
      onDuplicate({ folderId: choice === UNCAT ? null : choice });
    }
  };

  return (
    <div className="tb-modal-backdrop" onClick={onClose}>
      <div className="tb-modal" onClick={e => e.stopPropagation()}>
        <div className="tb-modal-head">
          <h3>Duplicate template</h3>
          <button className="tb-modal-close" onClick={onClose} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
        </div>
        <div className="tb-modal-body">
          <p className="tb-section-sub" style={{ marginBottom: 16 }}>A copy of <strong>{name}</strong> will be created. Choose the folder to put it in.</p>
          <div className="tb-field">
            <label className="tb-label">Folder</label>
            <select className="tb-input tb-select" value={choice} onChange={e => setChoice(e.target.value)}>
              <option value={UNCAT}>Uncategorized</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              <option value={NEW}>+ Create new folder…</option>
            </select>
          </div>
          {choice === NEW && (
            <div className="tb-field">
              <label className="tb-label">New folder name</label>
              <input className="tb-input" autoFocus placeholder="e.g. Onboarding" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') go(); }} />
            </div>
          )}
          <div className="tb-modal-footer">
            <button className="tb-btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary-cta" disabled={busy || (choice === NEW && !newName.trim())} onClick={go}>{busy ? 'Duplicating…' : 'Duplicate'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────── Tasks ─────────────────────────── */
const TasksTab: React.FC<{ content: OnbTemplateContent; update: (p: Partial<OnbTemplateContent>) => void }> = ({ content, update }) => {
  const { stages } = content;
  const setStages = (s: OnbTemplateStage[]) => update({ stages: s });

  const addStage = () => setStages([...stages, { id: uid(), name: 'New Stage', tasks: [] }]);
  const renameStage = (sid: string, name: string) => setStages(stages.map(s => s.id === sid ? { ...s, name } : s));
  const removeStage = (sid: string) => setStages(stages.filter(s => s.id !== sid));
  const addTask = (sid: string) => setStages(stages.map(s => s.id === sid ? { ...s, tasks: [...s.tasks, { id: uid(), title: 'New task' }] } : s));
  const renameTask = (sid: string, tid: string, title: string) => setStages(stages.map(s => s.id === sid ? { ...s, tasks: s.tasks.map(t => t.id === tid ? { ...t, title } : t) } : s));
  const removeTask = (sid: string, tid: string) => setStages(stages.map(s => s.id === sid ? { ...s, tasks: s.tasks.filter(t => t.id !== tid) } : s));

  return (
    <div className="tb-section">
      <h2 className="tb-section-title">Tasks</h2>

      {/* progress tracker */}
      <div className="tb-tracker">
        {stages.length === 0
          ? <><div className="tb-tracker-bar" /><span className="tb-tracker-hint"><InfoDot /> Add a stage to enable the progress tracker</span></>
          : <div className="tb-tracker-segments">{stages.map(s => <div key={s.id} className="tb-tracker-seg" title={s.name} />)}</div>}
      </div>

      {stages.length === 0 ? (
        <div className="tb-empty">
          <div className="tb-empty-ic">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9h10M7 13h6" /></svg>
          </div>
          <h3 className="tb-empty-title">Start by creating your first stage</h3>
          <p className="tb-empty-text">Create stages to organize your project, then add tasks within each stage.</p>
          <button className="tb-btn-outline" onClick={addStage}><PlusIc /> Add Stage</button>
        </div>
      ) : (
        <div className="tb-stages">
          {stages.map(s => (
            <div key={s.id} className="tb-stage">
              <div className="tb-stage-head">
                <input className="tb-stage-name" value={s.name} onChange={e => renameStage(s.id, e.target.value)} />
                <span className="tb-stage-count">{s.tasks.length} task{s.tasks.length !== 1 ? 's' : ''}</span>
                <button className="tb-icon-del" title="Delete stage" onClick={() => removeStage(s.id)}><TrashIc /></button>
              </div>
              <div className="tb-tasks">
                {s.tasks.map(t => (
                  <div key={t.id} className="tb-task-row">
                    <span className="tb-task-dot" />
                    <input className="tb-task-input" value={t.title} onChange={e => renameTask(s.id, t.id, e.target.value)} />
                    <button className="tb-icon-del sm" title="Delete task" onClick={() => removeTask(s.id, t.id)}><TrashIc /></button>
                  </div>
                ))}
                <button className="tb-add-inline" onClick={() => addTask(s.id)}><PlusIc /> Add task</button>
              </div>
            </div>
          ))}
          <button className="tb-btn-outline" onClick={addStage}><PlusIc /> Add Stage</button>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────── Meetings ───────────────────────── */
const MeetingsTab: React.FC<{ content: OnbTemplateContent; update: (p: Partial<OnbTemplateContent>) => void }> = ({ content, update }) => {
  const { meetings } = content;
  const [editing, setEditing] = useState<OnbTemplateMeeting | null>(null);
  const set = (m: OnbTemplateMeeting[]) => update({ meetings: m });

  const save = (m: OnbTemplateMeeting) => {
    set(meetings.some(x => x.id === m.id) ? meetings.map(x => x.id === m.id ? m : x) : [...meetings, m]);
    setEditing(null);
  };

  return (
    <div className="tb-section">
      <div className="tb-section-head">
        <div>
          <h2 className="tb-section-title">Meetings</h2>
          <p className="tb-section-sub">Meetings that projects created from this template will start with.</p>
        </div>
        <button className="btn-primary-cta" onClick={() => setEditing({ id: uid(), title: '', durationMins: 30, status: 'Scheduled', startTime: '9:00am', shareAgenda: true, shareTranscript: true, shareSummary: true, agenda: [] })}><PlusIc /> Add meeting</button>
      </div>

      {meetings.length === 0 ? (
        <div className="tb-empty">
          <div className="tb-empty-ic"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg></div>
          <h3 className="tb-empty-title">No meetings yet for this template</h3>
          <p className="tb-empty-text">Add agendas, decisions, and notes that carry into every project created from this template.</p>
        </div>
      ) : (
        <div className="tb-cards">
          {meetings.map(m => (
            <div key={m.id} className="tb-card" onClick={() => setEditing(m)}>
              <div className="tb-card-body">
                <div className="tb-card-title">{m.title || 'Untitled meeting'}</div>
                {m.description && <div className="tb-card-desc">{m.description}</div>}
                <div className="tb-card-meta"><ClockIc /> {m.durationMins ?? 30} min</div>
              </div>
              <button className="tb-icon-del" title="Delete" onClick={e => { e.stopPropagation(); set(meetings.filter(x => x.id !== m.id)); }}><TrashIc /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <MeetingModal
          value={editing}
          isNew={!meetings.some(x => x.id === editing.id)}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
};

const MEETING_STATUSES = ['Scheduled', 'Completed', 'Cancelled', 'No-show'];
const START_TIMES = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) for (const m of [0, 30]) {
    const ampm = h < 12 ? 'am' : 'pm';
    const hr = h % 12 === 0 ? 12 : h % 12;
    out.push(`${hr}:${m === 0 ? '00' : '30'}${ampm}`);
  }
  return out;
})();
const DURATIONS = ['15 min', '30 min', '45 min', '1 hr', '1.5 hr', '2 hr'];
const WORKFLOW_EVENTS = ['Scheduled', 'Completed', 'Cancelled'];
const WORKFLOW_ACTIONS = ['Send email', 'Create task', 'Notify team', 'Move stage'];

/* Full meeting editor — mirrors the reference "Create New Meeting" screen,
   presented as a large modal using the insight-modal look. */
const MeetingModal: React.FC<{ value: OnbTemplateMeeting; isNew: boolean; onClose: () => void; onSave: (m: OnbTemplateMeeting) => void }> = ({ value, isNew, onClose, onSave }) => {
  const [m, setM] = useState<OnbTemplateMeeting>({ ...value });
  const set = (patch: Partial<OnbTemplateMeeting>) => setM(prev => ({ ...prev, ...patch }));
  const agenda = m.agenda ?? [];
  const setAgenda = (a: OnbTemplateAgendaItem[]) => set({ agenda: a });

  const durationLabel = (() => {
    const map: Record<number, string> = { 15: '15 min', 30: '30 min', 45: '45 min', 60: '1 hr', 90: '1.5 hr', 120: '2 hr' };
    return map[m.durationMins ?? 30] ?? '30 min';
  })();
  const setDurationLabel = (label: string) => {
    const map: Record<string, number> = { '15 min': 15, '30 min': 30, '45 min': 45, '1 hr': 60, '1.5 hr': 90, '2 hr': 120 };
    set({ durationMins: map[label] ?? 30 });
  };

  return (
    <div className="tb-modal-backdrop" onClick={onClose}>
      <div className="tb-mtg-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="tb-mtg-head">
          <div className="tb-mtg-head-left">
            <button className="tb-mtg-back" onClick={onClose} title="Back"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg></button>
            <h3 className="tb-mtg-title">{isNew ? 'Create New Meeting' : 'Edit Meeting'}</h3>
          </div>
          <div className="tb-mtg-status">
            <label className="tb-mtg-status-label">Status:</label>
            <select className="tb-input tb-select tb-mtg-status-select" value={m.status ?? 'Scheduled'} onChange={e => set({ status: e.target.value })}>
              {MEETING_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="tb-mtg-body">
          {/* Row: Title / Attendees */}
          <div className="tb-mtg-grid">
            <div className="tb-field"><label className="tb-label">Title</label><input className="tb-input" autoFocus placeholder="Meeting title" value={m.title} onChange={e => set({ title: e.target.value })} /></div>
            <div className="tb-field"><label className="tb-label">Attendees</label><input className="tb-input" placeholder="Add attendees" value={(m.attendees ?? []).join(', ')} onChange={e => set({ attendees: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} /></div>
          </div>

          {/* Row: Date / Start time / Duration */}
          <div className="tb-mtg-grid tb-mtg-grid-3">
            <div className="tb-field"><label className="tb-label">Date</label><input className="tb-input" type="date" value={m.date ?? ''} onChange={e => set({ date: e.target.value })} /></div>
            <div className="tb-field"><label className="tb-label">Start time <span className="tb-label-hint">Pacific Time (UTC-8)</span></label>
              <select className="tb-input tb-select" value={m.startTime ?? '9:00am'} onChange={e => set({ startTime: e.target.value })}>{START_TIMES.map(t => <option key={t} value={t}>{t}</option>)}</select>
            </div>
            <div className="tb-field"><label className="tb-label">Duration</label>
              <select className="tb-input tb-select" value={durationLabel} onChange={e => setDurationLabel(e.target.value)}>{DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}</select>
            </div>
          </div>

          {/* Call-in */}
          <div className="tb-field tb-mtg-callin"><label className="tb-label">Call-in information <InfoDot /></label>
            <textarea className="tb-textarea" rows={2} placeholder="https://zoom.us/j/1234567890" value={m.callInInfo ?? ''} onChange={e => set({ callInInfo: e.target.value })} />
          </div>

          <div className="tb-mtg-divider" />

          {/* Agenda */}
          <div className="tb-mtg-section-head">
            <span className="tb-mtg-section-title">Agenda</span>
            <label className="tb-mtg-share"><span>Share in portal</span>
              <button type="button" role="switch" aria-checked={m.shareAgenda !== false} className={`tb-switch ${m.shareAgenda !== false ? 'on' : ''}`} onClick={() => set({ shareAgenda: !(m.shareAgenda !== false) })}><span className="tb-switch-knob" /></button>
            </label>
          </div>
          <div className="tb-mtg-agenda">
            {agenda.map(a => (
              <div key={a.id} className="tb-mtg-agenda-item">
                <span className="tb-mtg-agenda-dot" />
                <input className="tb-input" placeholder="Agenda item" value={a.text} onChange={e => setAgenda(agenda.map(x => x.id === a.id ? { ...x, text: e.target.value } : x))} />
                <button className="tb-icon-del sm" title="Remove" onClick={() => setAgenda(agenda.filter(x => x.id !== a.id))}><TrashIc /></button>
              </div>
            ))}
            <button className="tb-add-inline" onClick={() => setAgenda([...agenda, { id: uid(), text: '' }])}>+ Add agenda item</button>
          </div>

          <div className="tb-mtg-divider" />

          {/* Workflow */}
          <div className="tb-mtg-section-head"><span className="tb-mtg-section-title">Workflow</span></div>
          <div className="tb-mtg-workflow">
            <span className="tb-mtg-workflow-lbl">When this meeting is</span>
            <select className="tb-input tb-select tb-mtg-wf-select" value={m.workflowEvent ?? ''} onChange={e => set({ workflowEvent: e.target.value })}>
              <option value="">Event</option>{WORKFLOW_EVENTS.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            <span className="tb-mtg-workflow-arrow">→</span>
            <select className="tb-input tb-select tb-mtg-wf-select" value={m.workflowAction ?? ''} onChange={e => set({ workflowAction: e.target.value })}>
              <option value="">Action</option>{WORKFLOW_ACTIONS.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>

          <div className="tb-mtg-divider" />

          {/* Internal notes */}
          <div className="tb-mtg-section-head"><span className="tb-mtg-section-title">Internal notes <span className="tb-label-hint">Never shared</span></span></div>
          <textarea className="tb-textarea" rows={4} placeholder="Notes only visible to your team" value={m.internalNotes ?? ''} onChange={e => set({ internalNotes: e.target.value })} />

          <div className="tb-mtg-divider" />

          {/* Transcript */}
          <div className="tb-mtg-section-head">
            <span className="tb-mtg-section-title">Transcript</span>
            <label className="tb-mtg-share"><span>Share in portal</span>
              <button type="button" role="switch" aria-checked={m.shareTranscript !== false} className={`tb-switch ${m.shareTranscript !== false ? 'on' : ''}`} onClick={() => set({ shareTranscript: !(m.shareTranscript !== false) })}><span className="tb-switch-knob" /></button>
            </label>
          </div>
          <label className="tb-mtg-ai"><button type="button" role="switch" aria-checked={!!m.summarizeWithAi} className={`tb-switch ${m.summarizeWithAi ? 'on' : ''}`} onClick={() => set({ summarizeWithAi: !m.summarizeWithAi })}><span className="tb-switch-knob" /></button><span>Summarize with AI</span> <InfoDot /></label>
          <textarea className="tb-textarea tb-mtg-rich" rows={5} placeholder="Paste a call transcript, or enter your notes manually." value={m.transcript ?? ''} onChange={e => set({ transcript: e.target.value })} />

          <div className="tb-mtg-divider" />

          {/* Summary */}
          <div className="tb-mtg-section-head">
            <span className="tb-mtg-section-title">Summary</span>
            <label className="tb-mtg-share"><span>Share in portal</span>
              <button type="button" role="switch" aria-checked={m.shareSummary !== false} className={`tb-switch ${m.shareSummary !== false ? 'on' : ''}`} onClick={() => set({ shareSummary: !(m.shareSummary !== false) })}><span className="tb-switch-knob" /></button>
            </label>
          </div>
          <p className="tb-mtg-hint-text">No summary yet. Paste a transcript above and enable “Summarize with AI” to generate one, or write a summary below.</p>
          <textarea className="tb-textarea tb-mtg-rich" rows={5} placeholder="Meeting summary, decisions, and key points." value={m.summary ?? ''} onChange={e => set({ summary: e.target.value })} />
        </div>

        <div className="tb-mtg-footer">
          <button className="btn-primary-cta" disabled={!m.title.trim()} onClick={() => onSave({ ...m, title: m.title.trim(), agenda: agenda.filter(a => a.text.trim()) })}>Save meeting</button>
          <button className="tb-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────── Users ─────────────────────────── */
const UsersTab: React.FC<{ content: OnbTemplateContent; update: (p: Partial<OnbTemplateContent>) => void; members: OrgMember[] }> = ({ content, update, members }) => {
  const { users } = content;
  const [adding, setAdding] = useState(false);
  const set = (u: OnbTemplateUser[]) => update({ users: u });

  const addMember = (m: OrgMember) => {
    const name = m.full_name || m.email || 'Unnamed';
    if (users.some(u => u.email === m.email)) { setAdding(false); return; }
    set([...users, { id: uid(), name, email: m.email || '', role: m.role_name || m.role || 'Member' }]);
    setAdding(false);
  };

  return (
    <div className="tb-section">
      <h2 className="tb-section-title">Template Users</h2>
      <p className="tb-section-sub">Projects created from this template will inherit the team members you add below.</p>

      <div className="tb-users-table">
        <div className="tb-users-row tb-users-head">
          <span>Name</span><span>Email</span><span>Role</span><span className="tb-users-feat">Featured <InfoDot /></span><span className="tb-right">Actions</span>
        </div>
        {users.length === 0 ? (
          <div className="tb-empty-inline">No team members added yet.</div>
        ) : users.map(u => (
          <div key={u.id} className="tb-users-row">
            <span className="tb-user-name"><span className="tb-avatar">{initials(u.name)}</span>{u.name}</span>
            <span className="tb-muted">{u.email}</span>
            <span><span className="tb-role-pill">{u.role}</span></span>
            <span className="tb-users-feat">
              <input type="checkbox" checked={!!u.featured} onChange={e => set(users.map(x => x.id === u.id ? { ...x, featured: e.target.checked } : x))} />
            </span>
            <span className="tb-right"><button className="tb-icon-del" title="Remove" onClick={() => set(users.filter(x => x.id !== u.id))}><TrashIc /></button></span>
          </div>
        ))}
      </div>

      <div className="tb-users-actions">
        <button className="btn-primary-cta" onClick={() => setAdding(true)}><PlusIc /> Add team member to template</button>
        <button className="tb-btn-ghost" onClick={() => alert('Client welcome email preview is coming soon.')}><EyeIc /> View client welcome email</button>
      </div>

      {adding && (
        <SimpleModal title="Add team member" onClose={() => setAdding(false)}>
          <div className="tb-member-list">
            {members.length === 0 && <div className="tb-empty-inline">No organization members found.</div>}
            {members.map(m => {
              const already = users.some(u => u.email === m.email);
              const name = m.full_name || m.email || 'Unnamed';
              return (
                <button key={m.user_id} className="tb-member-item" disabled={already} onClick={() => addMember(m)}>
                  <span className="tb-avatar">{initials(name)}</span>
                  <span className="tb-member-info"><span className="tb-member-name">{name}</span><span className="tb-member-email">{m.email}</span></span>
                  <span className="tb-role-pill">{m.role_name || m.role}</span>
                  {already && <span className="tb-added">Added</span>}
                </button>
              );
            })}
          </div>
        </SimpleModal>
      )}
    </div>
  );
};

/* ─────────────────────────── Portal ────────────────────────── */
const PortalTab: React.FC<{ content: OnbTemplateContent; update: (p: Partial<OnbTemplateContent>) => void }> = ({ content, update }) => {
  const pages = content.portalPages.length ? content.portalPages : emptyTemplateContent().portalPages;
  const [activeId, setActiveId] = useState(pages[0]?.id);
  const active = pages.find(p => p.id === activeId) ?? pages[0];
  const set = (p: OnbTemplatePortalPage[]) => update({ portalPages: p });

  const [appOpen, setAppOpen] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const isOverview = !!active.system && active.id === 'overview';

  const addPage = () => {
    const np: OnbTemplatePortalPage = { id: uid(), title: 'New Page', content: '' };
    set([...pages, np]); setActiveId(np.id);
  };
  const patchActive = (patch: Partial<OnbTemplatePortalPage>) => set(pages.map(p => p.id === active.id ? { ...p, ...patch } : p));
  const removePage = (pid: string) => {
    const rest = pages.filter(p => p.id !== pid);
    set(rest); if (activeId === pid) setActiveId(rest[0]?.id);
  };

  const resources = active.resources ?? [];
  const setResources = (r: OnbTemplateResource[]) => patchActive({ resources: r });

  return (
    <div className="tb-section">
      <h2 className="tb-section-title">Portal Pages</h2>
      <p className="tb-section-sub">Add custom pages your client will see for projects created from this template.</p>

      <div className="tb-portal">
        <aside className="tb-portal-nav">
          <div className="tb-portal-nav-head">Client Portal Pages</div>
          {pages.map(p => (
            <div key={p.id} className={`tb-portal-nav-item ${active.id === p.id ? 'active' : ''}`} onClick={() => setActiveId(p.id)}>
              <span className="tb-portal-nav-name">{p.title}</span>
              {p.system
                ? null
                : <span className="tb-portal-nav-grip" title="Drag to reorder"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.4" /><circle cx="15" cy="6" r="1.4" /><circle cx="9" cy="12" r="1.4" /><circle cx="15" cy="12" r="1.4" /><circle cx="9" cy="18" r="1.4" /><circle cx="15" cy="18" r="1.4" /></svg></span>}
              {!p.system && <button className="tb-icon-del sm" title="Remove page" onClick={e => { e.stopPropagation(); removePage(p.id); }}><TrashIc /></button>}
            </div>
          ))}
          <button className="tb-portal-add" onClick={addPage}><PlusIc /> Add Page</button>
        </aside>

        <div className="tb-portal-main">
          <h3 className="tb-portal-page-h">{active.title || 'Page'}</h3>
          {isOverview && (
            <p className="tb-portal-page-desc">This page is the landing page for your project and will always be visible to the client. You may customize the main content and project resources to be shown in this page.</p>
          )}

          <input className="tb-portal-page-title" value={active.title} onChange={e => patchActive({ title: e.target.value })} disabled={isOverview} placeholder="Page title" />

          <label className="tb-label tb-portal-lbl">Content (optional) <InfoDot /></label>
          <RichEditor key={active.id} value={active.content ?? ''} onChange={html => patchActive({ content: html })} placeholder="Welcome message and instructions for your client…" />

          {/* App Embed (collapsible) */}
          <div className="tb-portal-collapse">
            <button className="tb-portal-collapse-head" onClick={() => setAppOpen(o => !o)}>
              <span>App Embed Code (optional)</span>
              <svg className={`tb-portal-chevron ${appOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {appOpen && (
              <div className="tb-portal-collapse-body">
                <p className="tb-portal-collapse-hint">Paste the URL or embed code provided by the app you want to show</p>
                <textarea className="tb-textarea" rows={3} placeholder="Website URL or the <iframe> code" value={active.appEmbed ?? ''} onChange={e => patchActive({ appEmbed: e.target.value })} />
              </div>
            )}
          </div>

          {/* Form Embed (collapsible) */}
          <div className="tb-portal-collapse">
            <button className="tb-portal-collapse-head" onClick={() => setFormOpen(o => !o)}>
              <span>Form Embed (optional)</span>
              <svg className={`tb-portal-chevron ${formOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="9 6 15 12 9 18" /></svg>
            </button>
            {formOpen && (
              <div className="tb-portal-collapse-body">
                <p className="tb-portal-collapse-hint">Paste a form embed URL or code to collect responses from your client.</p>
                <textarea className="tb-textarea" rows={3} placeholder="Form URL or embed code" value={active.formEmbed ?? ''} onChange={e => patchActive({ formEmbed: e.target.value })} />
              </div>
            )}
          </div>

          {/* Project Resources */}
          <div className="tb-portal-resources">
            <div className="tb-portal-res-head">
              <div>
                <div className="tb-portal-res-title">Project Resources</div>
                <p className="tb-portal-res-sub">Add important links and files for easy access by your clients on the Project Overview page in their client portal.</p>
              </div>
              <button className="tb-btn-outline" onClick={() => setResources([...resources, { id: uid(), name: '', url: '' }])}><PlusIc /> Add Resource</button>
            </div>
            <div className="tb-portal-res-table">
              <div className="tb-portal-res-row tb-portal-res-thead"><span>Resource Name</span><span>Link</span><span /></div>
              {resources.length === 0 ? (
                <div className="tb-empty-inline">No resources added yet.</div>
              ) : resources.map(r => (
                <div key={r.id} className="tb-portal-res-row">
                  <input className="tb-input" placeholder="Resource name" value={r.name} onChange={e => setResources(resources.map(x => x.id === r.id ? { ...x, name: e.target.value } : x))} />
                  <input className="tb-input" placeholder="https://…" value={r.url ?? ''} onChange={e => setResources(resources.map(x => x.id === r.id ? { ...x, url: e.target.value } : x))} />
                  <button className="tb-icon-del" title="Remove" onClick={() => setResources(resources.filter(x => x.id !== r.id))}><TrashIc /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────── Files ─────────────────────────── */
type FileFilter = 'all' | 'files' | 'folders';
const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso); if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const extOf = (name: string) => { const m = /\.([a-z0-9]+)$/i.exec(name); return m ? m[1].toLowerCase() : ''; };

const FilesTab: React.FC<{ content: OnbTemplateContent; update: (p: Partial<OnbTemplateContent>) => void }> = ({ content, update }) => {
  const files = content.files as OnbTemplateFile[];
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FileFilter>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [cwd, setCwd] = useState<string | null>(null);   // current folder id (null = root)
  const [menuId, setMenuId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  const set = (f: OnbTemplateFile[]) => update({ files: f });
  useEffect(() => {
    const h = (e: MouseEvent) => { if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false); setMenuId(null); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const q = search.trim().toLowerCase();
  const inCwd = files.filter(f => (f.parentId ?? null) === cwd);
  const filtered = inCwd
    .filter(f => filter === 'all' ? true : filter === 'folders' ? f.kind === 'folder' : f.kind !== 'folder')
    .filter(f => !q || f.name.toLowerCase().includes(q))
    .sort((a, b) => (a.kind === 'folder' ? 0 : 1) - (b.kind === 'folder' ? 0 : 1) || a.name.localeCompare(b.name));

  const currentFolder = files.find(f => f.id === cwd);

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!list.length) return;
    const now = new Date().toISOString();
    const add: OnbTemplateFile[] = [];
    let pending = list.length;
    list.forEach(file => {
      const meta: OnbTemplateFile = { id: uid(), name: file.name, kind: 'file', parentId: cwd, fileType: extOf(file.name), size: file.size, inPortal: false, createdAt: now, userInitial: 'U' };
      if (file.size <= 1024 * 1024) {
        const r = new FileReader();
        r.onload = () => { meta.url = String(r.result); add.push(meta); if (--pending === 0) set([...files, ...add]); };
        r.onerror = () => { add.push(meta); if (--pending === 0) set([...files, ...add]); };
        r.readAsDataURL(file);
      } else { add.push(meta); if (--pending === 0) set([...files, ...add]); }
    });
  };

  const newFolder = () => {
    const f: OnbTemplateFile = { id: uid(), name: 'New Folder', kind: 'folder', parentId: cwd, createdAt: new Date().toISOString() };
    set([...files, f]); setRenamingId(f.id); setRenameVal('New Folder');
  };
  const commitRename = (id: string) => { const nm = renameVal.trim(); if (nm) set(files.map(f => f.id === id ? { ...f, name: nm } : f)); setRenamingId(null); };
  const remove = (f: OnbTemplateFile) => {
    // delete the item and (if a folder) its descendants
    const toDelete = new Set([f.id]);
    if (f.kind === 'folder') files.forEach(x => { if (x.parentId === f.id) toDelete.add(x.id); });
    set(files.filter(x => !toDelete.has(x.id))); setMenuId(null);
  };
  const togglePortal = (f: OnbTemplateFile) => set(files.map(x => x.id === f.id ? { ...x, inPortal: !x.inPortal } : x));

  const FILTERS: { v: FileFilter; label: string }[] = [{ v: 'all', label: 'All Files' }, { v: 'files', label: 'Files only' }, { v: 'folders', label: 'Folders only' }];

  return (
    <div className="tb-section">
      <div className="tb-files-head">
        <div className="tb-files-crumbs">
          <button className="tb-files-crumb" onClick={() => setCwd(null)}>Project Files</button>
          {currentFolder && <><span className="tb-files-crumb-sep">/</span><span className="tb-files-crumb current">{currentFolder.name}</span></>}
        </div>
        <div className="tb-files-actions">
          <button className="tb-files-link" onClick={newFolder}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z" /><line x1="12" y1="10" x2="12" y2="16" /><line x1="9" y1="13" x2="15" y2="13" /></svg>
            New Folder
          </button>
          <button className="tb-btn-outline" onClick={() => inputRef.current?.click()}><UploadIc /> Upload Files</button>
          <input ref={inputRef} type="file" multiple hidden onChange={onUpload} />
        </div>
      </div>

      <div className="tb-files-toolbar">
        <div className="tb-files-filter" ref={filterRef}>
          <button className="tb-files-filter-btn" onClick={() => setFilterOpen(o => !o)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" /></svg>
            {FILTERS.find(f => f.v === filter)!.label}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>
          </button>
          {filterOpen && (
            <div className="tb-files-filter-menu">
              {FILTERS.map(f => <button key={f.v} className={`tb-files-filter-opt ${filter === f.v ? 'sel' : ''}`} onClick={() => { setFilter(f.v); setFilterOpen(false); }}>{f.label}</button>)}
            </div>
          )}
        </div>
        <div className="tb-search tb-files-search">
          <svg className="tb-search-ic" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input className="tb-search-input" placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="tb-files-table">
        <div className="tb-files-row tb-files-thead">
          <span className="tb-fc-name">Name</span>
          <span className="tb-fc-type">Type</span>
          <span className="tb-fc-user">User</span>
          <span className="tb-fc-task">Task</span>
          <span className="tb-fc-portal">In Portal <InfoDot /></span>
          <span className="tb-fc-created">Created</span>
          <span className="tb-fc-menu" />
        </div>

        {filtered.length === 0 ? (
          <div className="tb-empty-inline">{q ? 'No files match your search.' : 'No files yet. Upload files or create a folder.'}</div>
        ) : filtered.map(f => (
          <div key={f.id} className={`tb-files-row ${f.kind === 'folder' ? 'is-folder' : ''}`} onClick={() => f.kind === 'folder' && setCwd(f.id)}>
            <span className="tb-fc-name">
              <span className="tb-file-icon">
                {f.kind === 'folder'
                  ? <svg width="20" height="20" viewBox="0 0 24 24" fill="#facc15" stroke="#eab308" strokeWidth="1"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z" /></svg>
                  : (f.url && ['png','jpg','jpeg','gif','webp'].includes(f.fileType ?? '')
                    ? <img src={f.url} alt="" className="tb-file-thumb" />
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /></svg>)}
              </span>
              {renamingId === f.id
                ? <input className="tb-file-rename" autoFocus value={renameVal} onClick={e => e.stopPropagation()} onChange={e => setRenameVal(e.target.value)} onBlur={() => commitRename(f.id)} onKeyDown={e => { if (e.key === 'Enter') commitRename(f.id); if (e.key === 'Escape') setRenamingId(null); }} />
                : <span className="tb-file-nm">{f.name}</span>}
            </span>
            <span className="tb-fc-type">{f.kind === 'folder' ? '' : (f.url ? <span className="tb-file-uploaded" title="Uploaded"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5M12 3v12" /></svg></span> : <span className="tb-file-ext">{(f.fileType || '').toUpperCase()}</span>)}</span>
            <span className="tb-fc-user">{f.kind === 'folder' ? '' : <span className="tb-avatar tb-avatar-sm">{f.userInitial || 'U'}</span>}</span>
            <span className="tb-fc-task tb-muted">{f.task || ''}</span>
            <span className="tb-fc-portal" onClick={e => e.stopPropagation()}>
              {f.kind !== 'folder' && <button type="button" role="switch" aria-checked={!!f.inPortal} className={`tb-switch tb-switch-sm ${f.inPortal ? 'on' : ''}`} onClick={() => togglePortal(f)}><span className="tb-switch-knob" /></button>}
            </span>
            <span className="tb-fc-created tb-muted">{fmtDate(f.createdAt)}</span>
            <span className="tb-fc-menu" onClick={e => e.stopPropagation()}>
              <button className="tb-file-menu-btn" onClick={() => setMenuId(menuId === f.id ? null : f.id)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
              </button>
              {menuId === f.id && (
                <div className="tb-file-menu">
                  <button onClick={() => { setRenamingId(f.id); setRenameVal(f.name); setMenuId(null); }}>Rename</button>
                  {f.kind !== 'folder' && f.url && <a href={f.url} download={f.name} onClick={() => setMenuId(null)}>Download</a>}
                  <button className="danger" onClick={() => remove(f)}>Delete</button>
                </div>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────── Notes ─────────────────────────── */
const NotesTab: React.FC<{ content: OnbTemplateContent; update: (p: Partial<OnbTemplateContent>) => void }> = ({ content, update }) => {
  const { notes } = content;
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<OnbTemplateNote | null>(null);
  const set = (n: OnbTemplateNote[]) => update({ notes: n });
  const q = search.trim().toLowerCase();
  const stripHtml = (h?: string) => (h ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const shown = q ? notes.filter(n => (n.title + ' ' + stripHtml(n.body)).toLowerCase().includes(q)) : notes;

  const save = (n: OnbTemplateNote) => {
    const now = new Date().toISOString();
    const exists = notes.some(x => x.id === n.id);
    const withMeta = { ...n, updatedAt: now, createdAt: n.createdAt ?? now };
    set(exists ? notes.map(x => x.id === n.id ? withMeta : x) : [...notes, withMeta]);
    setEditing(null);
  };

  return (
    <div className="tb-section">
      <div className="tb-section-head">
        <h2 className="tb-section-title">Notes</h2>
        <button className="btn-primary-cta" onClick={() => setEditing({ id: uid(), title: '', body: '' })}><PlusIc /> Add New</button>
      </div>

      <div className="tb-search">
        <svg className="tb-search-ic" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
        <input className="tb-search-input" placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {shown.length === 0 ? (
        <div className="tb-empty">
          <div className="tb-empty-ic"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h12l4 4v12H4z" /><path d="M8 9h8M8 13h8M8 17h5" /></svg></div>
          <h3 className="tb-empty-title">{q ? 'No notes match your search' : 'No notes yet'}</h3>
          {!q && <p className="tb-empty-text">Click “Add New” to create one.</p>}
        </div>
      ) : (
        <div className="tb-note-list">
          {shown.map(n => (
            <div key={n.id} className="tb-note-card" onClick={() => setEditing(n)}>
              <div className="tb-note-title">{n.title || 'Untitled note'}</div>
              {stripHtml(n.body) && <div className="tb-note-body">{stripHtml(n.body)}</div>}
              <button className="tb-icon-del" title="Delete" onClick={e => { e.stopPropagation(); set(notes.filter(x => x.id !== n.id)); }}><TrashIc /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <NoteModal value={editing} isNew={!notes.some(x => x.id === editing.id)} onClose={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
};

/* Note popup — icon + title + Create, with a rich-text editor (matches ref). */
const NoteModal: React.FC<{ value: OnbTemplateNote; isNew: boolean; onClose: () => void; onSave: (n: OnbTemplateNote) => void }> = ({ value, isNew, onClose, onSave }) => {
  const [title, setTitle] = useState(value.title);
  const [body, setBody] = useState(value.body ?? '');
  const submit = () => { if (title.trim()) onSave({ ...value, title: title.trim(), body }); };

  return (
    <div className="tb-modal-backdrop" onClick={onClose}>
      <div className="tb-note-modal" onClick={e => e.stopPropagation()}>
        <div className="tb-note-modal-head">
          <span className="tb-note-modal-ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /></svg></span>
          <input className="tb-note-modal-title" autoFocus placeholder="Note title" value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
          <button className="btn-primary-cta tb-note-create-btn" disabled={!title.trim()} onClick={submit}>{isNew ? 'Create' : 'Save'}</button>
          <button className="tb-modal-close" onClick={onClose} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
        </div>
        <div className="tb-note-modal-body">
          <RichEditor value={body} onChange={setBody} placeholder="Enter note content…" />
        </div>
      </div>
    </div>
  );
};

/* Lightweight rich-text editor: toolbar (Paragraph / B / I / link / clear /
   bullet / numbered / align / image) over a contentEditable surface. */
const RichEditor: React.FC<{ value: string; onChange: (html: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => {
  const ref = useRef<HTMLDivElement>(null);
  const imgInput = useRef<HTMLInputElement>(null);
  // Seed once; subsequent typing is uncontrolled to preserve the caret.
  useEffect(() => { if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || ''; /* eslint-disable-next-line */ }, []);
  const exec = (cmd: string, arg?: string) => { document.execCommand(cmd, false, arg); ref.current?.focus(); sync(); };
  const sync = () => { if (ref.current) onChange(ref.current.innerHTML); };
  const onBlock = (e: React.ChangeEvent<HTMLSelectElement>) => { exec('formatBlock', e.target.value); e.target.selectedIndex = 0; };
  const addLink = () => { const url = window.prompt('Enter URL'); if (url) exec('createLink', url); };
  const pickImg = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f || !f.type.startsWith('image/')) return;
    if (f.size > 1024 * 1024) { alert('Please choose an image under 1 MB.'); return; }
    const r = new FileReader(); r.onload = () => exec('insertImage', String(r.result)); r.readAsDataURL(f);
  };

  return (
    <div className="tb-rich">
      <div className="tb-rich-toolbar">
        <select className="tb-rich-block" onChange={onBlock} defaultValue="">
          <option value="" disabled hidden>Paragraph</option>
          <option value="<p>">Paragraph</option>
          <option value="<h1>">Heading 1</option>
          <option value="<h2>">Heading 2</option>
          <option value="<h3>">Heading 3</option>
        </select>
        <span className="tb-rich-sep" />
        <button type="button" className="tb-rich-btn" title="Bold" onClick={() => exec('bold')}><b>B</b></button>
        <button type="button" className="tb-rich-btn" title="Italic" onClick={() => exec('italic')}><i>I</i></button>
        <button type="button" className="tb-rich-btn" title="Link" onClick={addLink}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" /></svg></button>
        <button type="button" className="tb-rich-btn" title="Clear formatting" onClick={() => exec('removeFormat')}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h12M9 4l-2 16M5 20h6" /><line x1="18" y1="14" x2="22" y2="18" /><line x1="22" y1="14" x2="18" y2="18" /></svg></button>
        <span className="tb-rich-sep" />
        <button type="button" className="tb-rich-btn" title="Bullet list" onClick={() => exec('insertUnorderedList')}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="8" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="20" y2="12" /><line x1="8" y1="18" x2="20" y2="18" /><circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="4" cy="12" r="1" fill="currentColor" /><circle cx="4" cy="18" r="1" fill="currentColor" /></svg></button>
        <button type="button" className="tb-rich-btn" title="Numbered list" onClick={() => exec('insertOrderedList')}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="10" y1="6" x2="20" y2="6" /><line x1="10" y1="12" x2="20" y2="12" /><line x1="10" y1="18" x2="20" y2="18" /><path d="M4 6h1v4M4 10h2" /></svg></button>
        <button type="button" className="tb-rich-btn" title="Align" onClick={() => exec('justifyCenter')}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="5" y1="18" x2="19" y2="18" /></svg></button>
        <span className="tb-rich-sep" />
        <button type="button" className="tb-rich-btn" title="Insert image" onClick={() => imgInput.current?.click()}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg></button>
        <input ref={imgInput} type="file" accept="image/*" hidden onChange={pickImg} />
      </div>
      <div
        ref={ref}
        className="tb-rich-area"
        contentEditable
        data-placeholder={placeholder}
        onInput={sync}
        suppressContentEditableWarning
      />
    </div>
  );
};

/* ────────────────────────── Settings ───────────────────────── */
type SettingsSub = 'general' | 'emails' | 'fields';
const SettingsTab: React.FC<{
  content: OnbTemplateContent;
  update: (p: Partial<OnbTemplateContent>) => void;
  members: OrgMember[];
  name: string;
  isPublic: boolean;
  onTogglePublic: (v: boolean) => void;
  onRename: (v: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}> = ({ content, update, members, name, isPublic, onTogglePublic, onRename, onDuplicate, onDelete }) => {
  const [sub, setSub] = useState<SettingsSub>('general');
  const s: OnbTemplateSettings = content.settings ?? {};
  const setS = (patch: Partial<OnbTemplateSettings>) => update({ settings: { ...s, ...patch } });
  const memberName = (m: OrgMember) => m.full_name || m.email || 'Unnamed';

  // General: local name field (commits on Save settings / blur)
  const [nameDraft, setNameDraft] = useState(name);
  useEffect(() => { setNameDraft(name); }, [name]);

  // Project icon upload (stored as a data URL in settings.icon)
  const iconInputRef = useRef<HTMLInputElement>(null);
  const onIconPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please choose an image file.'); return; }
    if (file.size > 1024 * 1024) { alert('Please choose an image under 1 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => setS({ icon: String(reader.result) });
    reader.readAsDataURL(file);
  };
  const owner = members.find(m => m.user_id === s.projectOwnerId);
  const ownerLabel = owner ? memberName(owner) : (s.fieldProjectOwner || '');

  const SUBS: { key: SettingsSub; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'emails',  label: 'Emails' },
    { key: 'fields',  label: 'Fields' },
  ];

  return (
    <div className="tb-set">
      <div className="tb-subtabs" role="tablist">
        {SUBS.map(t => (
          <button key={t.key} role="tab" aria-selected={sub === t.key} className={`tb-subtab ${sub === t.key ? 'active' : ''}`} onClick={() => setSub(t.key)}>{t.label}</button>
        ))}
      </div>

      {sub === 'general' && (
        <div className="tb-set-panel">
          <div className="tb-set-icon-row">
            <span className="tb-set-icon">
              {s.icon
                ? <img src={s.icon} alt="Project icon" className="tb-set-icon-img" />
                : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /></svg>}
            </span>
            <button className="tb-btn-outline" onClick={() => iconInputRef.current?.click()}>{s.icon ? 'Change Project Icon' : 'Upload Project Icon'}</button>
            {s.icon && <button className="tb-btn-ghost" onClick={() => setS({ icon: '' })}>Remove</button>}
            <input ref={iconInputRef} type="file" accept="image/*" hidden onChange={onIconPick} />
          </div>

          <div className="tb-field tb-set-field">
            <label className="tb-label">Name</label>
            <input className="tb-input" value={nameDraft} onChange={e => setNameDraft(e.target.value)} onBlur={() => nameDraft.trim() && nameDraft !== name && onRename(nameDraft)} />
          </div>

          <div className="tb-field tb-set-field">
            <label className="tb-label">Project owner <InfoDot /></label>
            <select className="tb-input tb-select" value={s.projectOwnerId ?? ''} onChange={e => { const v = e.target.value || null; const m = members.find(x => x.user_id === v); setS({ projectOwnerId: v, fieldProjectOwner: m ? memberName(m) : '' }); }}>
              <option value="">Select a project owner</option>
              {members.map(m => <option key={m.user_id} value={m.user_id}>{memberName(m)}</option>)}
            </select>
          </div>

          <div className="tb-field tb-set-field-wide">
            <label className="tb-label">Description</label>
            <textarea className="tb-textarea" rows={5} placeholder="Describe how the template will be used" value={s.description ?? ''} onChange={e => setS({ description: e.target.value })} />
          </div>

          <div className="tb-toggle-row">
            <div>
              <div className="tb-toggle-label">Publish to Public Templates</div>
              <div className="tb-toggle-help">Make this template available to everyone in your organization under the Public Templates tab.</div>
            </div>
            <button type="button" role="switch" aria-checked={isPublic} className={`tb-switch ${isPublic ? 'on' : ''}`} onClick={() => onTogglePublic(!isPublic)}>
              <span className="tb-switch-knob" />
            </button>
          </div>

          <div className="tb-set-actions">
            <button className="btn-primary-cta" onClick={() => nameDraft.trim() && onRename(nameDraft)}>Save settings</button>
            <button className="tb-btn-outline" onClick={onDuplicate}>Duplicate Template</button>
            <button className="tb-btn-danger-outline" onClick={onDelete}>Delete</button>
          </div>
        </div>
      )}

      {sub === 'emails' && (
        <div className="tb-set-panel">
          <p className="tb-section-sub">Personalize your project emails.</p>
          <div className="tb-email-card">
            <div className="tb-email-head">Client Welcome Email</div>
            <p className="tb-email-note">The following email will be sent to clients upon inviting them to this portal.</p>
            <div className="tb-field"><label className="tb-label">Subject</label>
              <input className="tb-input" value={s.welcomeEmailSubject ?? ''} onChange={e => setS({ welcomeEmailSubject: e.target.value })} />
            </div>
            <div className="tb-field"><label className="tb-label">Content</label>
              <textarea className="tb-textarea" rows={8} value={s.welcomeEmailContent ?? ''} onChange={e => setS({ welcomeEmailContent: e.target.value })} />
            </div>
            <div className="tb-email-actions">
              <button className="btn-primary-cta" onClick={() => alert('Saved.')}>Save</button>
              <button className="tb-btn-outline" onClick={() => alert('Test email sending is coming soon.')}>Send Test Email</button>
              <button className="tb-btn-ai" onClick={() => alert('AI Email Writer is coming soon.')}><SparkIc /> AI Email Writer</button>
            </div>
          </div>
        </div>
      )}

      {sub === 'fields' && (
        <div className="tb-set-panel">
          <p className="tb-section-sub">Set field values for this project, which can be utilized via dynamic tags within the project welcome email, task descriptions, and portal pages to dynamically insert the project’s values.</p>
          <p className="tb-note">Note: Fields have a max length of 255 characters.</p>
          <div className="tb-fields-table">
            <div className="tb-fields-row tb-fields-head"><span>Field</span><span>Value</span><span>Slug <InfoDot /></span></div>
            <div className="tb-fields-row">
              <span className="tb-field-name">Project Owner</span>
              <input className="tb-input" maxLength={255} value={ownerLabel} onChange={e => setS({ fieldProjectOwner: e.target.value })} />
              <SlugCell slug="{{PROJECT_OWNER}}" />
            </div>
            <div className="tb-fields-row">
              <span className="tb-field-name">Project Name</span>
              <input className="tb-input" maxLength={255} value={s.fieldProjectName || name} onChange={e => setS({ fieldProjectName: e.target.value })} />
              <SlugCell slug="{{PROJECT_NAME}}" />
            </div>
          </div>
          <button className="btn-primary-cta" onClick={() => alert('Saved.')}>Save field values</button>
        </div>
      )}
    </div>
  );
};

const SlugCell: React.FC<{ slug: string }> = ({ slug }) => (
  <span className="tb-slug-cell">
    <code className="tb-slug">{slug}</code>
    <button className="tb-slug-copy" title="Copy" onClick={() => navigator.clipboard?.writeText(slug)}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
    </button>
  </span>
);

const SparkIc = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" /><path d="M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" /></svg>;

/* ─────────────────────── Shared bits ───────────────────────── */
const SimpleModal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="tb-modal-backdrop" onClick={onClose}>
    <div className="tb-modal" onClick={e => e.stopPropagation()}>
      <div className="tb-modal-head">
        <h3>{title}</h3>
        <button className="tb-modal-close" onClick={onClose} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
      </div>
      <div className="tb-modal-body">{children}</div>
    </div>
  </div>
);

const PlusIc = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const TrashIc = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>;
const ClockIc = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
const UploadIc = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5M12 3v12" /></svg>;
const EyeIc = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>;
const InfoDot = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></svg>;

export default OnboardingTemplateBuilder;
